import React, { useState, useEffect } from 'react';
import { Plus, Users, Edit2, Trash2, UserPlus, UserMinus, X, Search, CheckCircle, UserCheck, Award } from 'lucide-react';
import useMyTeamStore from '@/stores/AdminStore/MyTeamStore';
import { Team, AvailableUser } from '@/services/PagesServices/MyTeamService';
import { AdminOrganizationService } from '@/services/AdminService/AdminOrganizationService';
import { OrganizationOption } from '@/types/assignment';
import './ManageTeamsTab.css';

const ManageTeamsTab: React.FC = () => {
  const {
    teams,
    selectedTeam,
    teamMembers,
    availableUsers,
    loading,
    error,
    fetchAllTeams,
    fetchTeamById,
    createNewTeam,
    updateExistingTeam,
    deleteExistingTeam,
    fetchAvailableUsers,
    addMembersToTeam,
    removeMemberFromTeam,
    setSelectedTeam,
    clearError,
    setTeamManagerById,
  } = useMyTeamStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignManagerModal, setShowAssignManagerModal] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [teamOrgId, setTeamOrgId] = useState<number | ''>('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  // Separate search state for the Assign Manager modal so it doesn't leak
  // into / get polluted by the Add Members modal's filter.
  const [managerSearchQuery, setManagerSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  useEffect(() => {
    if (showCreateModal && organizations.length === 0 && !orgsLoading) {
      setOrgsLoading(true);
      AdminOrganizationService.listMyOrganizations()
        .then(setOrganizations)
        .catch((err) => console.error('orgs fetch failed', err))
        .finally(() => setOrgsLoading(false));
    }
  }, [showCreateModal, organizations.length, orgsLoading]);

  // When the admin belongs to exactly one organization, auto-apply it and
  // hide the picker (the dropdown would only have a single option anyway).
  useEffect(() => {
    if (showCreateModal && organizations.length === 1 && teamOrgId === '') {
      setTeamOrgId(organizations[0].id);
    }
  }, [showCreateModal, organizations, teamOrgId]);

  useEffect(() => {
    fetchAllTeams();
  }, [fetchAllTeams]);

  useEffect(() => {
    if (error) {
      setTimeout(() => clearError(), 5000);
    }
  }, [error, clearError]);

  useEffect(() => {
    if (successMessage) {
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [successMessage]);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) return;

    const result = await createNewTeam(
      teamName,
      teamDescription,
      teamOrgId === '' ? null : Number(teamOrgId),
      null
    );
    if (result.success) {
      setShowCreateModal(false);
      setTeamName('');
      setTeamDescription('');
      setTeamOrgId('');
      setSuccessMessage('Team created successfully!');
    }
  };

  const handleAssignManager = async () => {
    if (!selectedTeam) return;
    const ok = await setTeamManagerById(selectedTeam.id, selectedManagerId || null);
    if (ok) {
      setShowAssignManagerModal(false);
      setSelectedManagerId('');
      setManagerSearchQuery('');
      setSuccessMessage(selectedManagerId ? 'Manager assigned successfully!' : 'Manager cleared.');
    }
  };

  const handleUpdateTeam = async () => {
    if (!selectedTeam || !teamName.trim()) return;

    const success = await updateExistingTeam(selectedTeam.id, teamName, teamDescription);
    if (success) {
      setShowEditModal(false);
      setTeamName('');
      setTeamDescription('');
      setSuccessMessage('Team updated successfully!');
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      const success = await deleteExistingTeam(teamId);
      if (success) {
        setSelectedTeam(null);
        setSuccessMessage('Team deleted successfully!');
      }
    }
  };

  const handleViewTeam = async (team: Team) => {
    await fetchTeamById(team.id);
  };

  const handleOpenAddMembers = async () => {
    if (!selectedTeam) return;
    // Pass the current team so users already on a different team still surface
    // — only members of THIS team are filtered out by the backend.
    await fetchAvailableUsers(selectedTeam.id);
    setSelectedUserIds([]);
    setSearchQuery('');
    setShowAddMembersModal(true);
  };

  const handleAddMembers = async () => {
    if (!selectedTeam || selectedUserIds.length === 0) return;

    const result = await addMembersToTeam(selectedTeam.id, selectedUserIds);
    if (result.success) {
      setShowAddMembersModal(false);
      setSelectedUserIds([]);
      setSuccessMessage(`${result.addedCount} member(s) added successfully!`);
      // Refresh team details
      await fetchTeamById(selectedTeam.id);
    }
  };

  const handleRemoveMember = async (userId: string, userName: string) => {
    if (!selectedTeam) return;

    if (window.confirm(`Remove ${userName} from this team?`)) {
      const success = await removeMemberFromTeam(selectedTeam.id, userId);
      if (success) {
        setSuccessMessage('Member removed successfully!');
        await fetchTeamById(selectedTeam.id);
      }
    }
  };

  const handlePromoteToManager = async (userId: string, userName: string) => {
    if (!selectedTeam) return;
    if (!window.confirm(`Make ${userName} the manager of ${selectedTeam.name}?`)) return;
    const ok = await setTeamManagerById(selectedTeam.id, userId);
    if (ok) {
      setSuccessMessage(`${userName} is now the manager of ${selectedTeam.name}.`);
      await fetchTeamById(selectedTeam.id);
      await fetchAllTeams();
    }
  };

  const handleEditTeam = (team: Team) => {
    setTeamName(team.name);
    setTeamDescription(team.description || '');
    setShowEditModal(true);
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Be liberal with what we match: users imported via CSV without first/last
  // names previously came back as a single space and never matched anything.
  // Now the backend falls back to email-local-part for `name`, and we also
  // search firstName/lastName/jobTitle/department + the email-local prefix
  // so a query like "alice" matches alice@acme.com even if her profile is empty.
  const filteredAvailableUsers = availableUsers?.filter((user) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const emailLocal = (user.email || '').split('@')[0] || '';
    return (
      (user.name || '').toLowerCase().includes(q) ||
      (user.email || '').toLowerCase().includes(q) ||
      emailLocal.toLowerCase().includes(q) ||
      ((user as any).firstName || '').toLowerCase().includes(q) ||
      ((user as any).lastName || '').toLowerCase().includes(q) ||
      (user.jobTitle || '').toLowerCase().includes(q) ||
      (user.department || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="manage-teams-container">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="manage-teams-success-message">
          <CheckCircle size={16} />
          {successMessage}
        </div>
      )}
      {error && (
        <div className="manage-teams-error-message">
          {error}
        </div>
      )}

      <div className="manage-teams-layout">
        {/* Teams List Panel */}
        <div className="manage-teams-list-panel">
          <div className="manage-teams-list-header">
            <h3>Teams</h3>
            <button
              className="manage-teams-create-btn"
              onClick={() => {
                setTeamName('');
                setTeamDescription('');
                setShowCreateModal(true);
              }}
            >
              <Plus size={16} />
              Create Team
            </button>
          </div>

          <div className="manage-teams-list">
            {loading && !teams ? (
              <div className="manage-teams-loading">Loading teams...</div>
            ) : teams?.length === 0 ? (
              <div className="manage-teams-empty">
                <Users size={48} />
                <p>No teams created yet</p>
                <button
                  className="manage-teams-create-first-btn"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Your First Team
                </button>
              </div>
            ) : (
              teams?.map(team => (
                <div
                  key={team.id}
                  className={`manage-teams-item ${selectedTeam?.id === team.id ? 'active' : ''}`}
                  onClick={() => handleViewTeam(team)}
                >
                  <div className="manage-teams-item-icon">
                    <Users size={20} />
                  </div>
                  <div className="manage-teams-item-info">
                    <div className="manage-teams-item-name">{team.name}</div>
                    <div className="manage-teams-item-count">{team.memberCount} members</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Team Details Panel */}
        <div className="manage-teams-detail-panel">
          {selectedTeam ? (
            <>
              <div className="manage-teams-detail-header">
                <div className="manage-teams-detail-title">
                  <h2>{selectedTeam.name}</h2>
                  {selectedTeam.description && (
                    <p className="manage-teams-detail-desc">{selectedTeam.description}</p>
                  )}
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 13,
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Award size={14} color="#059669" />
                    <span>
                      <strong>Manager:</strong>{' '}
                      {selectedTeam.manager_name || (
                        <span style={{ color: '#6b7280', fontStyle: 'italic' }}>
                          Not set — click "Make Manager" on a member below to assign
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="manage-teams-detail-actions">
                  <button
                    className="manage-teams-action-btn edit"
                    onClick={() => {
                      setSelectedManagerId((selectedTeam as any).manager_id || '');
                      setManagerSearchQuery('');
                      setShowAssignManagerModal(true);
                    }}
                    title="Pick a manager from a searchable list"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: 'auto', padding: '0 10px' }}
                  >
                    <UserCheck size={16} />
                    <span style={{ fontSize: 13 }}>Reassign Manager</span>
                  </button>
                  <button
                    className="manage-teams-action-btn edit"
                    onClick={() => handleEditTeam(selectedTeam)}
                    title="Edit Team"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className="manage-teams-action-btn delete"
                    onClick={() => handleDeleteTeam(selectedTeam.id)}
                    title="Delete Team"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="manage-teams-members-section">
                <div className="manage-teams-members-header">
                  <h3>Team Members ({teamMembers?.length || 0})</h3>
                  <button
                    className="manage-teams-add-member-btn"
                    onClick={handleOpenAddMembers}
                  >
                    <UserPlus size={16} />
                    Add Members
                  </button>
                </div>

                <div className="manage-teams-members-list">
                  {teamMembers?.length === 0 ? (
                    <div className="manage-teams-no-members">
                      <p>No members in this team yet</p>
                      <button
                        className="manage-teams-add-first-btn"
                        onClick={handleOpenAddMembers}
                      >
                        Add Members
                      </button>
                    </div>
                  ) : (
                    teamMembers?.map(member => {
                      const isCurrentManager = (selectedTeam as any).manager_id === member.id;
                      return (
                        <div key={member.id} className="manage-teams-member-card">
                          <div className="manage-teams-member-avatar">{member.initials}</div>
                          <div className="manage-teams-member-info">
                            <div
                              className="manage-teams-member-name"
                              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                            >
                              {member.name}
                              {isCurrentManager && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    background: '#10b981',
                                    color: '#fff',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                  }}
                                >
                                  <Award size={12} /> Manager
                                </span>
                              )}
                            </div>
                            <div className="manage-teams-member-email">{member.email}</div>
                            <div className="manage-teams-member-meta">
                              {member.jobTitle} • {member.department}
                            </div>
                          </div>
                          <div className="manage-teams-member-stats">
                            <div className="manage-teams-member-progress">
                              <span>{member.progress}%</span>
                              <div className="manage-teams-progress-bar">
                                <div
                                  className="manage-teams-progress-fill"
                                  style={{ width: `${member.progress}%` }}
                                />
                              </div>
                            </div>
                            <div className="manage-teams-member-courses">
                              {member.completedCourses} completed
                            </div>
                          </div>
                          {!isCurrentManager && (
                            <button
                              onClick={() => handlePromoteToManager(member.id, member.name)}
                              title={`Make ${member.name} the manager of this team`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                background: '#fff',
                                border: '1px solid #10b981',
                                color: '#047857',
                                fontSize: 12,
                                fontWeight: 600,
                                padding: '6px 10px',
                                borderRadius: 6,
                                cursor: 'pointer',
                                marginRight: 6,
                              }}
                            >
                              <Award size={14} /> Make Manager
                            </button>
                          )}
                          <button
                            className="manage-teams-remove-btn"
                            onClick={() => handleRemoveMember(member.id, member.name)}
                            title="Remove from team"
                          >
                            <UserMinus size={16} />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="manage-teams-no-selection">
              <Users size={64} />
              <h3>Select a Team</h3>
              <p>Choose a team from the list to view its members and details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="manage-teams-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="manage-teams-modal" onClick={e => e.stopPropagation()}>
            <div className="manage-teams-modal-header">
              <h3>Create New Team</h3>
              <button className="manage-teams-close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="manage-teams-modal-body">
              <div className="manage-teams-form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={e => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                  autoFocus
                />
              </div>
              <div className="manage-teams-form-group">
                <label>Description</label>
                <textarea
                  value={teamDescription}
                  onChange={e => setTeamDescription(e.target.value)}
                  placeholder="Enter team description (optional)"
                  rows={3}
                />
              </div>
              {organizations.length !== 1 && (
                <div className="manage-teams-form-group">
                  <label>Organization</label>
                  <select
                    value={teamOrgId}
                    onChange={(e) => setTeamOrgId(e.target.value === '' ? '' : Number(e.target.value))}
                  >
                    <option value="">{orgsLoading ? 'Loading...' : 'No organization'}</option>
                    {organizations.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Optional. Assignments scoped to this team must come from a manager in the same organization.
                  </div>
                </div>
              )}
            </div>
            <div className="manage-teams-modal-footer">
              <button
                className="manage-teams-cancel-btn"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button
                className="manage-teams-submit-btn"
                onClick={handleCreateTeam}
                disabled={!teamName.trim() || loading}
              >
                {loading ? 'Creating...' : 'Create Team'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Manager Modal */}
      {showAssignManagerModal && selectedTeam && (
        <div className="manage-teams-modal-overlay" onClick={() => setShowAssignManagerModal(false)}>
          <div className="manage-teams-modal manage-teams-modal-large" onClick={e => e.stopPropagation()}>
            <div className="manage-teams-modal-header">
              <h3>Assign Manager — {selectedTeam.name}</h3>
              <button className="manage-teams-close-btn" onClick={() => setShowAssignManagerModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="manage-teams-modal-body">
              <div className="manage-teams-search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by name, email, job title, or department..."
                  value={managerSearchQuery}
                  onChange={(e) => setManagerSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              {(() => {
                const q = managerSearchQuery.trim().toLowerCase();
                const matches = (teamMembers || []).filter((m: any) => {
                  if (!q) return true;
                  return (
                    (m.name || '').toLowerCase().includes(q) ||
                    (m.email || '').toLowerCase().includes(q) ||
                    (m.jobTitle || '').toLowerCase().includes(q) ||
                    (m.department || '').toLowerCase().includes(q)
                  );
                });

                if (!teamMembers || teamMembers.length === 0) {
                  return (
                    <div style={{ padding: 16, color: '#6b7280' }}>
                      Add members to this team first, then assign one as the manager.
                    </div>
                  );
                }

                if (matches.length === 0) {
                  return (
                    <div style={{ padding: 16, color: '#6b7280', textAlign: 'center' }}>
                      No members match "{managerSearchQuery.trim()}".
                    </div>
                  );
                }

                return (
                  <div className="manage-teams-users-list">
                    {matches.map((m: any) => {
                      const isCurrent = (selectedTeam as any).manager_id === m.id;
                      return (
                        <div
                          key={m.id}
                          className={`manage-teams-user-item ${selectedManagerId === m.id ? 'selected' : ''}`}
                          onClick={() => setSelectedManagerId(m.id)}
                        >
                          <div className="manage-teams-user-checkbox">
                            {selectedManagerId === m.id && <CheckCircle size={18} />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                              {m.name}
                              {isCurrent && (
                                <span
                                  style={{
                                    background: '#10b981',
                                    color: '#fff',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    padding: '2px 6px',
                                    borderRadius: 999,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                  }}
                                >
                                  Current
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{m.email}</div>
                            {(m.jobTitle || m.department) && (
                              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                {[m.jobTitle, m.department].filter(Boolean).join(' • ')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="manage-teams-modal-footer">
              <button
                className="manage-teams-cancel-btn"
                onClick={() => { setShowAssignManagerModal(false); setSelectedManagerId(''); setManagerSearchQuery(''); }}
              >
                Cancel
              </button>
              <button
                className="manage-teams-submit-btn"
                onClick={handleAssignManager}
                disabled={loading || !selectedManagerId}
              >
                {loading ? 'Saving...' : 'Assign Manager'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && (
        <div className="manage-teams-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="manage-teams-modal" onClick={e => e.stopPropagation()}>
            <div className="manage-teams-modal-header">
              <h3>Edit Team</h3>
              <button className="manage-teams-close-btn" onClick={() => setShowEditModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="manage-teams-modal-body">
              <div className="manage-teams-form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={e => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                  autoFocus
                />
              </div>
              <div className="manage-teams-form-group">
                <label>Description</label>
                <textarea
                  value={teamDescription}
                  onChange={e => setTeamDescription(e.target.value)}
                  placeholder="Enter team description (optional)"
                  rows={3}
                />
              </div>
            </div>
            <div className="manage-teams-modal-footer">
              <button
                className="manage-teams-cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="manage-teams-submit-btn"
                onClick={handleUpdateTeam}
                disabled={!teamName.trim() || loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Members Modal */}
      {showAddMembersModal && (
        <div className="manage-teams-modal-overlay" onClick={() => setShowAddMembersModal(false)}>
          <div className="manage-teams-modal manage-teams-modal-large" onClick={e => e.stopPropagation()}>
            <div className="manage-teams-modal-header">
              <h3>Add Members to {selectedTeam?.name}</h3>
              <button className="manage-teams-close-btn" onClick={() => setShowAddMembersModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="manage-teams-modal-body">
              <div className="manage-teams-search-box">
                <Search size={16} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, job title, or department..."
                  autoFocus
                />
              </div>

              {selectedUserIds.length > 0 && (
                <div className="manage-teams-selected-count">
                  {selectedUserIds.length} user(s) selected
                </div>
              )}

              <div className="manage-teams-users-list">
                {(!availableUsers || availableUsers.length === 0) ? (
                  <div className="manage-teams-no-users">
                    No learners available to add. Either everyone in your organization is already on this team, or no learners exist yet.
                  </div>
                ) : filteredAvailableUsers?.length === 0 ? (
                  <div className="manage-teams-no-users">
                    No users match "{searchQuery.trim()}". Try a different name, email, or department.
                  </div>
                ) : (
                  filteredAvailableUsers?.map(user => (
                    <div
                      key={user.id}
                      className={`manage-teams-user-item ${selectedUserIds.includes(user.id) ? 'selected' : ''}`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="manage-teams-user-checkbox">
                        {selectedUserIds.includes(user.id) && <CheckCircle size={18} />}
                      </div>
                      <div className="manage-teams-user-info">
                        <div className="manage-teams-user-name">{user.name}</div>
                        <div className="manage-teams-user-email">{user.email}</div>
                        <div className="manage-teams-user-meta">
                          {user.jobTitle} • {user.department}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="manage-teams-modal-footer">
              <button
                className="manage-teams-cancel-btn"
                onClick={() => setShowAddMembersModal(false)}
              >
                Cancel
              </button>
              <button
                className="manage-teams-submit-btn"
                onClick={handleAddMembers}
                disabled={selectedUserIds.length === 0 || loading}
              >
                {loading ? 'Adding...' : `Add ${selectedUserIds.length} Member(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTeamsTab;
